<template>
  <div class="rising-sign-container">
    <div class="mx-auto w-full max-w-[1100px] min-h-screen pb-12 pt-4">
      <!-- masthead -->
      <AdController unit="masthead" />

      <!-- content -->
      <div class="w-full flex mt-6 max-w-[1100px]">
        <!-- body -->
        <div class="text-white w-full max-w-[717px]">
          <div class="px-4 sm:px-0">
            <ArticleBreadcrumb />
            <h1 class="text-24 sm:text-28 font-bold text-white tracking-tight leading-snug mt-8">{{ titlePrefix }} Burç Uyumu</h1>
            <AstrologyArticleAuthor :authors="article.authors" class="mt-8" />
            <div v-if="!isResultPage" class="text-24 font-normal mt-6">Uyumu görmek için burçları seçin</div>
            <div v-if="!isResultPage" class="text-md font-normal mt-4 mb-3">
              Burç uyumluluğu, astrolojide iki kişinin güneş burçlarına (ve bazen ay, yükselen, venüs burçlarına) göre ilişkide ne kadar iyi
              anlaşabileceğini yorumlamaya çalışan bir inanç sistemidir. Kimi insanlar için sadece eğlencelik bir sohbet konusu olsa da,
              kimileri için ciddiye alınan bir ilişki filtresidir.
            </div>
            <CompatibilityForm :hide-actions="isResultPage" class="mt-6" @update="handleUpdateForm" />
            <h2 v-if="!isResultPage" class="text-24 font-normal mt-6">Burç Uyumu Nedir?</h2>
            <div v-if="!isResultPage" class="text-md font-normal mt-4 mb-3">
              Burç uyumu, astrolojide iki kişinin doğum haritalarındaki burçlarının birbirleriyle ne kadar iyi anlaştığını değerlendiren bir
              kavramdır. En basit anlamıyla, "bu iki burç birbirine uygun mu?" sorusunun cevabını arar. Bu uyum, çoğunlukla güneş burçları
              (yani halk arasında bilinen burçlar) üzerinden değerlendirilir ama aslında daha derin analizlerde ay burcu, yükselen burç,
              Venüs ve Mars yerleşimleri de çok önemlidir.
            </div>
            <h2 v-if="!isResultPage" class="text-24 font-normal mt-6">Astrolojik Olarak Burç Uyumu Var Mı?</h2>
            <div v-if="!isResultPage" class="text-md font-normal mt-4 mb-3">
              Astrolojik olarak burç uyumu, insanların burçlarına göre birbirleriyle ne kadar iyi anlaştığını gösteren bir yaklaşımdır. Her
              burcun kendine özgü karakter özellikleri vardır; bu özellikler bazı burçlarla uyumlu olurken bazılarıyla çatışabilir. Örneğin,
              ateş burçları (Koç, Aslan, Yay) enerjik ve girişken yapılarıyla genellikle birbirleriyle iyi anlaşırken, toprak burçları
              (Boğa, Başak, Oğlak) daha sabırlı ve dengeli yapılarıyla yine kendi grupları içinde daha uyumlu kabul edilir.
              <br />
              <br />
              Bu uyum değerlendirmeleri genellikle burçların element gruplarına (ateş, toprak, hava, su) ve benzer karakter yapılarına göre
              yapılır. Mesela duygusal ve hassas olan su burçları (Yengeç, Akrep, Balık) kendilerini anlayan burçlarla daha iyi ilişkiler
              kurabilirken, mantık odaklı hava burçları (İkizler, Terazi, Kova) iletişim kurmayı seven burçlarla daha iyi anlaşır. Bu
              nedenle burç uyumu, iki kişinin burçlarının birbiriyle nasıl etkileşim kurduğuna dair genel bir fikir verir.
            </div>
            <OImage
              v-if="!isResultPage"
              src="https://srv-cdn.onedio.com/store/a8f6728d28c3d619ef30072c0d5054bcb6da7d88130376e6db351e159afe33b4.png"
              class="w-full h-auto sm:w-[715px] sm:h-[375px] mt-6 px-4 sm:px-0 object-cover"
            />
            <h2 v-if="!isResultPage" class="text-24 font-normal mt-4">Astroloji ve Aşk Uyumu</h2>
            <div v-if="!isResultPage" class="text-md font-normal mt-4 mb-3">
              Astrolojide aşk uyumu, iki kişinin doğum anındaki gökyüzü konumlarının birbirleriyle olan ilişkisine bakılarak
              değerlendirilir. Bu değerlendirme, kişilerin duygusal yapıları, sevgi dili, ilişki beklentileri ve iletişim tarzları gibi
              unsurlar üzerinden yapılır. Astrolojik uyum, yalnızca güneş burçlarına değil; Ay, Venüs, Mars gibi gezegenlerin konumlarına da
              dayanır.
              <br />
              <br />
              Amaç, iki kişinin enerjilerinin birbirini nasıl etkilediğini ve tamamlayıp tamamlamadığını anlamaktır. Bu uyum, sadece
              benzerliklere değil; farklılıkların nasıl dengelendiğine de bakarak ilişki potansiyelini yorumlamaya çalışır. Astroloji burada
              kesin sonuçlar vermektense, ilişkiyi daha iyi anlama ve kişisel farkındalık kazanma aracı olarak kullanılır.
            </div>
            <CompabilityRateSlider v-if="isResultPage" :rate="compatibilityData.score" />
            <div v-if="isResultPage" class="mt-4 mb-3">
              <div v-for="entry in compatibilityData.entries" :key="entry.id">
                <div class="text-28 font-bold">{{ entry.title }}</div>
                <div class="text-lg font-normal mt-4 mb-3">{{ entry.description }}</div>
              </div>
            </div>
            <CompatibilityForm v-if="isResultPage" hide-images @update="handleUpdateForm" />
          </div>

          <OArticle :item="article" :hide="['meta', 'breadcrumb', 'tags', 'kunye', 'author', 'entries', 'title']" />
          <!-- compatibility widget -->
          <CompabilityWidget v-if="isResultPage" :horoscope="horoscope" />
          <!-- frequency widget cards -->
          <AstrologyWidgetCard :horoscope="horoscope" />
          <TarotTypesSwiper class="sm:hidden px-4 mt-6" />
        </div>
        <!-- aside -->
        <aside class="text-white hidden sm:block sm:w-[344px] sm:ml-9">
          <AdController unit="newSidebarSponTop" class="bg-[#f7f8fb] p-6 sticky z-1 top-24 xmt-8" />
          <AstrologyPopularCard />
          <!-- other tarot types -->
          <div class="sticky z-1 pt-4 top-[400px] bg-black">
            <TarotTypesSwiper />
          </div>
        </aside>
      </div>
    </div>

    <!-- astrology CTA -->
    <div v-if="isResultPage" class="sm:h-[400px] w-full bg-[#080C25] u-center py-6 sm:py-16 px-4 sm:px-0">
      <div class="mx-auto w-full max-w-[1100px] u-center">
        <AstrologyCTA horoscope-name="Koç" horoscope="aries" type="love" frequency="daily" />
      </div>
    </div>

    <!-- kunye -->
    <div class="h-[176px] sm:h-52 w-full bg-black u-center px-4 sm:px-0">
      <div class="mx-auto w-full max-w-[1100px] u-center">
        <AstrologyArticleAuthor :authors="article.authors" is-kunye />
      </div>
    </div>

    <!-- reactions and comments -->
    <div class="w-full bg-[#101116] u-center">
      <div class="mx-auto w-full max-w-[1100px]">
        <LazyHydrate :when-visible="{ rootMargin: '100px' }">
          <OArticleReactions class="mt-10 mb-6" :item="article"></OArticleReactions>
        </LazyHydrate>
        <LazyHydrate :when-visible="{ rootMargin: '300px' }">
          <OArticleComments :id="'reactions-' + article.legacyId" class="mb-10" :item="article"></OArticleComments>
        </LazyHydrate>
      </div>
    </div>

    <!-- more info tarot -->
    <TarotMoreInfoCard v-if="false" />
  </div>
</template>

<script>
import { defineComponent, ref, useRoute } from '@nuxtjs/composition-api';
import ErrorPageLayout from '@/modules/common/components/layout/ErrorPageLayout';
import OArticle from '@/modules/article/components/OArticle.vue';
import ArticleBreadcrumb from '@/modules/article/components/ArticleBreadcrumb';
import AdController from '@/modules/ads/components/AdController';
import LazyHydrate from 'vue-lazy-hydration';
import TarotTypesSwiper from '@/modules/tarot/components/TarotTypesSwiper.vue';
import TarotMoreInfoCard from '@/modules/tarot/components/TarotMoreInfoCard.vue';
import AstrologyArticleAuthor from '@/modules/astrology/components/AstrologyArticleAuthor.vue';
import AstrologyPopularCard from '@/modules/astrology/components/AstrologyPopularCard.vue';
import AstrologyWidgetCard from '@/modules/astrology/components/AstrologyWidgetCard.vue';
import AstrologyCTA from '@/modules/astrology/components/AstrologyCTA.vue';
import OArticleComments from '@/modules/article/components/OArticleComments.vue';
import OArticleReactions from '@/modules/article/components/OArticleReactions.vue';
import CompatibilityForm from '@/modules/astrology/components/CompatibilityForm.vue';
import CompabilityRateSlider from '@/modules/astrology/components/CompabilityRateSlider.vue';
import CompabilityWidget from '@/modules/astrology/components/CompabilityWidget.vue';
import { OImage, OIcon } from '@/modules/common/components/ui';
import { getHoroscopeName } from '~/modules/astrology/utils/astrologyUtils';

export default defineComponent({
  components: {
    ErrorPageLayout,
    OArticle,
    OArticleReactions,
    OArticleComments,
    ArticleBreadcrumb,
    AstrologyArticleAuthor,
    AstrologyPopularCard,
    AstrologyWidgetCard,
    AstrologyCTA,
    LazyHydrate,
    AdController,
    TarotTypesSwiper,
    TarotMoreInfoCard,
    CompatibilityForm,
    CompabilityRateSlider,
    CompabilityWidget,
    OImage,
    OIcon,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    compatibilityData: {
      type: Object,
      required: false,
    },
  },
  setup() {
    const route = useRoute();
    const horoscope1 = route.value.params.pathMatch ? route.value.params.pathMatch.split('-')[0] : null;
    const horoscope2 = route.value.params.pathMatch ? route.value.params.pathMatch.split('-')[1] : null;
    const titlePrefix = horoscope1 && horoscope2 ? `${getHoroscopeName(horoscope1)} & ${getHoroscopeName(horoscope2)} Aşk` : '';
    const isResultPage = !!horoscope1 && !!horoscope2;

    const horoscope = ref(horoscope1 || 'aries');
    const handleUpdateForm = (value) => {
      horoscope.value = value;
    };
    return {
      horoscope,
      handleUpdateForm,
      titlePrefix,
      isResultPage,
    };
  },
});
</script>

<style lang="scss">
@import '~/assets/css/custom-article.scss';
.rising-sign-container {
  background: linear-gradient(180deg, #a22b80 0%, #000 12%);
}
</style>
